import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api';
import { useAuthStore } from '@/stores/auth';
import type { CheckIn, RecognitionWithUsers, SafeUser } from '@ultisim/shared';
import { MOOD_EMOJIS, MOOD_LABELS } from '@ultisim/shared';
import { format, startOfWeek, endOfWeek } from 'date-fns';

interface TimesheetInfo {
  id: string;
  status: string;
  totalHours: number;
  weekStart: string;
  weekEnd: string;
}

interface TaskInfo {
  id: string;
  title: string;
  priority: string;
  dueDate: string | null;
  project?: { name: string } | null;
}

export default function DashboardPage() {
  const { user } = useAuthStore();
  const [timesheet, setTimesheet] = useState<TimesheetInfo | null>(null);
  const [tasks, setTasks] = useState<TaskInfo[]>([]);
  const [checkin, setCheckin] = useState<CheckIn | null>(null);
  const [recognitions, setRecognitions] = useState<RecognitionWithUsers[]>([]);
  const [users, setUsers] = useState<SafeUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Quick recognition form
  const [showGiveRecognition, setShowGiveRecognition] = useState(false);
  const [recognitionTo, setRecognitionTo] = useState('');
  const [recognitionMsg, setRecognitionMsg] = useState('');
  const [sendingRecognition, setSendingRecognition] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingRecId, setEditingRecId] = useState<string | null>(null);
  const [editingRecMsg, setEditingRecMsg] = useState('');

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  useEffect(() => {
    const loadData = async () => {
      try {
        const dateStr = format(now, 'yyyy-MM-dd');
        const [timesheetResult, taskResult, checkinResult, recResult, userResult] = await Promise.all([
          api.get<TimesheetInfo>('/timesheets/week', { date: dateStr }).catch(() => null),
          api.get<TaskInfo[]>('/tasks', { status: 'pending' }),
          api.get<CheckIn | null>('/checkins/week', { date: dateStr }),
          api.get<RecognitionWithUsers[]>('/recognitions', { limit: '10' }),
          api.get<SafeUser[]>('/users').catch(() => []),
        ]);
        setTimesheet(timesheetResult);
        setTasks(taskResult.slice(0, 5));
        setCheckin(checkinResult);
        setRecognitions(recResult);
        setUsers(Array.isArray(userResult) ? userResult.filter((u: SafeUser) => u.id !== user?.id) : []);
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const sendRecognition = async () => {
    if (!recognitionTo || !recognitionMsg.trim()) return;
    setSendingRecognition(true);
    try {
      await api.post('/recognitions', { toUserId: recognitionTo, message: recognitionMsg.trim() });
      setShowGiveRecognition(false);
      setRecognitionTo('');
      setRecognitionMsg('');
      const recResult = await api.get<RecognitionWithUsers[]>('/recognitions', { limit: '10' });
      setRecognitions(recResult);
    } catch (err) {
      console.error('Failed to send recognition:', err);
    } finally {
      setSendingRecognition(false);
    }
  };

  const hoursLogged = timesheet?.totalHours || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">{greeting}, {user?.firstName}!</h1>
        <p className="text-dark-400 mt-1">{format(now, 'EEEE, MMMM d, yyyy')}</p>
      </div>

      {/* Top row: Timesheet status + Check-in */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Timesheet status */}
        <Link to="/time-entry" className="card hover:border-primary-500/30 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-dark-400">This Week's Timesheet</h2>
            <span className={`badge ${
              timesheet?.status === 'approved' ? 'badge-success' :
              timesheet?.status === 'submitted' ? 'badge-primary' :
              timesheet?.status === 'rejected' ? 'badge-danger' :
              'badge-gray'
            }`}>
              {timesheet?.status || 'Not started'}
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{hoursLogged}h</p>
          <p className="text-xs text-dark-500 mt-2">
            {format(weekStart, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
          </p>
        </Link>

        {/* Weekly check-in */}
        <Link to="/check-in" className="card hover:border-primary-500/30 transition-colors">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-dark-400">Weekly Check-in</h2>
            {checkin ? (
              <span className="badge badge-success">Done</span>
            ) : (
              <span className="badge badge-gray">Not yet</span>
            )}
          </div>
          {checkin ? (
            <div>
              <p className="text-3xl mb-1">{checkin.mood ? MOOD_EMOJIS[checkin.mood] : '—'}</p>
              <p className="text-sm text-dark-300">{checkin.mood ? MOOD_LABELS[checkin.mood] : 'No mood set'}</p>
              {checkin.wins && <p className="text-xs text-dark-500 mt-2 truncate">Wins: {checkin.wins}</p>}
            </div>
          ) : (
            <div>
              <p className="text-dark-400">How was your week?</p>
              <p className="text-sm text-primary-400 mt-2">Share your wins, challenges & mood →</p>
            </div>
          )}
        </Link>
      </div>

      {/* Tasks + Recognition */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tasks */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">My Tasks</h2>
            <Link to="/tasks" className="text-sm text-primary-400 hover:text-primary-300">View all</Link>
          </div>
          {tasks.length === 0 ? (
            <p className="text-dark-500 text-center py-6">No pending tasks</p>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div key={task.id} className="flex items-start gap-3 p-3 bg-dark-800 rounded-lg">
                  <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-amber-500' :
                    task.priority === 'medium' ? 'bg-primary-500' : 'bg-dark-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-dark-200 text-sm truncate">{task.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {task.project && <span className="text-xs text-dark-500">{task.project.name}</span>}
                      {task.dueDate && (
                        <span className={`text-xs ${new Date(task.dueDate) < now ? 'text-red-400' : 'text-dark-500'}`}>
                          Due {format(new Date(task.dueDate), 'MMM d')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recognition feed */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              {showArchived ? 'Archived High Fives 📦' : 'High Fives 🙌'}
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={async () => {
                  const next = !showArchived;
                  setShowArchived(next);
                  const recResult = await api.get<RecognitionWithUsers[]>('/recognitions', { limit: '20', archived: String(next) });
                  setRecognitions(recResult);
                }}
                className="text-sm text-dark-400 hover:text-dark-200"
              >
                {showArchived ? '← Recent' : 'Archived'}
              </button>
              {!showArchived && (
                <button onClick={() => setShowGiveRecognition(!showGiveRecognition)} className="text-sm text-primary-400 hover:text-primary-300">
                  {showGiveRecognition ? 'Cancel' : '+ Give one'}
                </button>
              )}
            </div>
          </div>

          {showGiveRecognition && (
            <div className="mb-4 p-3 bg-dark-800 rounded-lg space-y-3">
              <select value={recognitionTo} onChange={(e) => setRecognitionTo(e.target.value)} className="input text-sm w-full">
                <option value="">Select recipient...</option>
                <option value="team">The Team 🎉 (#kudos only)</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                ))}
              </select>
              <input
                type="text"
                value={recognitionMsg}
                onChange={(e) => setRecognitionMsg(e.target.value)}
                placeholder="What did they do great?"
                className="input text-sm w-full"
                onKeyDown={(e) => { if (e.key === 'Enter') sendRecognition(); }}
              />
              <button onClick={sendRecognition} disabled={sendingRecognition || !recognitionTo || !recognitionMsg.trim()} className="btn-primary btn-sm w-full">
                {sendingRecognition ? 'Sending...' : 'Send High Five 🙌'}
              </button>
            </div>
          )}

          {recognitions.length === 0 ? (
            <p className="text-dark-500 text-center py-6">
              {showArchived ? 'No archived high fives yet' : 'No high fives this week — be the first!'}
            </p>
          ) : (
            <div className="space-y-3">
              {recognitions.map((rec) => (
                <div key={rec.id} className="p-3 bg-dark-800 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-primary-400">{rec.fromUser.firstName} {rec.fromUser.lastName}</span>
                      <span className="text-dark-600">→</span>
                      <span className="text-sm font-medium text-accent-400">{rec.toUser.firstName} {rec.toUser.lastName}</span>
                    </div>
                    {rec.fromUserId === user?.id && editingRecId !== rec.id && (
                      <button
                        onClick={() => { setEditingRecId(rec.id); setEditingRecMsg(rec.message); }}
                        className="text-xs text-dark-500 hover:text-dark-300"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                  {editingRecId === rec.id ? (
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={editingRecMsg}
                        onChange={(e) => setEditingRecMsg(e.target.value)}
                        className="input text-sm py-1 px-2 flex-1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setEditingRecId(null);
                          if (e.key === 'Enter' && editingRecMsg.trim()) {
                            api.put(`/recognitions/${rec.id}`, { message: editingRecMsg.trim() }).then(async () => {
                              setEditingRecId(null);
                              const recResult = await api.get<RecognitionWithUsers[]>('/recognitions', { limit: '20', archived: String(showArchived) });
                              setRecognitions(recResult);
                            });
                          }
                        }}
                      />
                      <button
                        onClick={async () => {
                          if (!editingRecMsg.trim()) return;
                          await api.put(`/recognitions/${rec.id}`, { message: editingRecMsg.trim() });
                          setEditingRecId(null);
                          const recResult = await api.get<RecognitionWithUsers[]>('/recognitions', { limit: '20', archived: String(showArchived) });
                          setRecognitions(recResult);
                        }}
                        className="btn-sm btn-primary py-1"
                      >
                        Save
                      </button>
                      <button onClick={() => setEditingRecId(null)} className="btn-sm btn-ghost py-1">Cancel</button>
                    </div>
                  ) : (
                    <p className="text-sm text-dark-300">"{rec.message}"</p>
                  )}
                  <p className="text-xs text-dark-600 mt-1">{format(new Date(rec.createdAt), 'MMM d')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
